import { FC } from 'react';
import { alpha } from '@mui/material/styles';
import { Paper, Stack, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useDroppable } from '@dnd-kit/core';

export interface DropZoneProps {
  /** Unique id passed to useDroppable */
  droppableId: string;
  /** Extra data attached to the droppable (forwarded to dnd-kit) */
  droppableData?: Record<string, unknown>;
  /**
   * null  = nothing being dragged
   * true  = dragged type is compatible
   * false = dragged type is incompatible
   */
  canAccept: boolean | null;
  /** The type string of whatever is currently being dragged */
  draggingType: string | null;
  /** Message shown when the dragged type is incompatible */
  incompatibleMessage: string;
  /** Default prompt shown when nothing is being dragged */
  placeholder: string;
  onClick: () => void;
}

/**
 * Shared droppable zone used by all question types.
 *
 * Encapsulates the `useDroppable` hook so callers don't have to wire it up
 * manually. The border + background tints green (compatible), red
 * (incompatible) or neutral (idle) based on `canAccept` + hover state.
 * @param root0
 * @param root0.droppableId
 * @param root0.droppableData
 * @param root0.canAccept
 * @param root0.draggingType
 * @param root0.incompatibleMessage
 * @param root0.placeholder
 * @param root0.onClick
 */
export const DropZone: FC<DropZoneProps> = ({
  droppableId,
  droppableData = {},
  canAccept,
  draggingType,
  incompatibleMessage,
  placeholder,
  onClick,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: droppableData,
  });

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: canAccept === false ? 'not-allowed' : 'pointer',
        border: '2px dashed',
        transition: 'all 0.15s',
        borderColor:
          isOver && canAccept !== false
            ? 'success.main'
            : isOver
              ? 'error.main'
              : canAccept === true
                ? 'success.main'
                : canAccept === false
                  ? 'error.main'
                  : 'divider',
        backgroundColor: (theme: any) =>
          isOver && canAccept !== false
            ? alpha(theme.palette.success.main, 0.12)
            : isOver
              ? alpha(theme.palette.error.main, 0.12)
              : canAccept === true
                ? alpha(theme.palette.success.main, 0.07)
                : canAccept === false
                  ? alpha(theme.palette.error.main, 0.07)
                  : 'transparent',
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: 'primary.main',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          color:
            canAccept === true
              ? 'success.main'
              : canAccept === false
                ? 'error.main'
                : 'text.secondary',
        }}
      >
        <AddIcon />
        <Typography variant="body2">
          {canAccept === false && draggingType
            ? incompatibleMessage
            : placeholder}
        </Typography>
      </Stack>
    </Paper>
  );
};
